import React from 'react'
import {
  Footer,
  FooterCopy,
  FooterNavigation,
  FooterDivider,
  SocialNavigation,
  Icon,
} from 'belle-js'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import styles from './BaseFooter.module.scss'
import { trackAnalyticsEvent } from 'analytics'
import PrivateWealthEmailLink from '../PrivateWealthEmailLink/PrivateWealthEmailLink'

const socialIconSize = 45

const BaseFooter = (props) => {
  const {
    sidebarDataLoaded,
    footerCopy,
    showPricing,
    showTel,
    showMorningstarLogo,
  } = props

  const footerClasses = classnames(
    styles.BaseFooterContainer,
    sidebarDataLoaded ? styles.moveRight : undefined,
  )

  const footerNavigation = classnames(
    sidebarDataLoaded ? styles.container : 'container',
    styles.BaseFooter
  )

  return (
    <div className={footerClasses}>
      <div className={footerNavigation}>
        <Footer>
          { footerCopy && (
            <div className={styles.footerTopCopy} data-test-id="footer-top-copy">
              <FooterCopy data-test-id="footer-top-copy-text">
                {footerCopy}
              </FooterCopy>
              <div className={styles.divider}>
                <FooterDivider data-test-id="footer-top-copy-divider" />
              </div>
            </div>
          ) }
          <div className={styles.footerNavigation}>
            <FooterNavigation data-test-id="footer-navigation">
              <li data-test-id="footer-navigation-product">
                <span>Service</span>
                <ul>
                  <li>
                    <a
                      onClick={() => trackAnalyticsEvent('Footer FAQ Clicked')}
                      href="https://ellevest.zendesk.com/"
                      target="_blank"
                      rel="noopener noreferrer">
                    FAQ
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.ellevest.com/privacy_policy"
                      onClick={() => trackAnalyticsEvent('Footer Privacy Clicked')}>
                      Privacy
                    </a>
                  </li>
                  { showPricing && (
                    <li data-test-id="footer-navigation-pricing">
                      <a href="https://www.ellevest.com/pricing">
                        Pricing
                      </a>
                    </li>
                  ) }
                </ul>
              </li>
              <li data-test-id="footer-navigation-contact">
                <span>Contact</span>
                <ul>
                  <li>
                    <PrivateWealthEmailLink
                      onClick={() => trackAnalyticsEvent('Footer PWM Email Clicked')} />
                  </li>
                  { showTel && (
                    <li data-test-id="footer-navigation-tel">
                      <a href="tel:18443557100">
                        844-ELL-7100
                      </a>
                    </li>
                  ) }
                </ul>
              </li>
              <li data-test-id="footer-navigation-company">
                <span>Company</span>
                <ul>
                  <li>
                    <a
                      href="https://www.ellevest.com/careers"
                      onClick={() => trackAnalyticsEvent('Footer Careers Clicked')}>
                      Careers
                    </a>
                  </li>
                  <li>
                    <a
                      href="mailto:press@ellevest.com"
                      onClick={() => trackAnalyticsEvent('Footer Press Email Clicked')}>
                      press@ellevest.com
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.ellevest.com/terms-of-service"
                      onClick={() => trackAnalyticsEvent('Footer Terms of Service Clicked')}>
                      Terms of Service
                    </a>
                  </li>
                  <li>
                    <a
                      onClick={() => trackAnalyticsEvent('Footer ADV Clicked')}
                      href="http://production.assets.ellevest.com/documents/Ellevest-Wrap-Fee-Program-Brochure-20180227.pdf"
                      target="_blank"
                      rel="noreferrer noopener">
                      Form ADV Part 2
                    </a>
                  </li>
                </ul>
              </li>
              { showMorningstarLogo && (
                <li data-test-id="footer-navigation-morningstar">
                  <span>In Partnership With</span>
                  <br />
                  <img
                    src="/images/logo_morningstar.png"
                    width="150"
                    alt="Morningstar Logo"
                    style={{ marginTop: 10 }} />
                </li>
              ) }
              <li data-test-id="footer-navigation-digicert">
                <img
                  src="/images/digicert_seal.png"
                  alt="Digicert Seal Logo" />
              </li>
            </FooterNavigation>
          </div>

          <div className={styles.footerSocialNavigation}>
            <SocialNavigation layout="inline" faded={true} data-test-id="footer-social-navigation">
              <li>
                <a
                  onClick={() => trackAnalyticsEvent('Footer LinkedIn Icon Clicked')}
                  href="https://www.linkedin.com/company/10208395"
                  rel="noopener noreferrer"
                  target="_blank">
                  <Icon icon="linkedin_circle" width={socialIconSize} />
                </a>
              </li>
              <li>
                <a
                  onClick={() => trackAnalyticsEvent('Footer Twitter Icon Clicked')}
                  href="https://twitter.com/ellevest"
                  rel="noopener noreferrer"
                  target="_blank">
                  <Icon icon="twitter_circle" width={socialIconSize} />
                </a>
              </li>
              <li>
                <a
                  onClick={() => trackAnalyticsEvent('Footer Facebook Icon Clicked')}
                  href="https://www.facebook.com/ellevesting"
                  rel="noopener noreferrer"
                  target="_blank">
                  <Icon icon="facebook_circle" width={socialIconSize} />
                </a>
              </li>
              <li>
                <a
                  onClick={() => trackAnalyticsEvent('Footer Instagram Icon Clicked')}
                  href="https://www.instagram.com/ellevest"
                  rel="noopener noreferrer"
                  target="_blank">
                  <Icon icon="instagram_circle" width={socialIconSize} />
                </a>
              </li>
              <li>
                <a
                  href="http://www.digital.nyc/"
                  target="_blank"
                  rel="noopener noreferrer">
                  <img
                    src="/images/logo_madeinny.png"
                    alt="Made in NY Logo"
                    width={socialIconSize}
                    style={{
                      width: socialIconSize,
                      height: socialIconSize,
                      display: 'block',
                    }} />
                </a>
              </li>
            </SocialNavigation>
          </div>

          <FooterCopy data-test-id="footer-bottom-copy">
            <p>
              © 2015-2018 Ellevest, Inc. All rights reserved. Your
              use of this site signifies that you accept our website <a href="https://www.ellevest.com/terms-of-service">terms of service.</a>
            </p>
          </FooterCopy>
        </Footer>
      </div>
    </div>
  )
}

BaseFooter.defaultProps = {
  footerCopy: null,
  showPricing: false,
  showTel: false,
  showMorningstarLogo: false,
}

BaseFooter.propTypes = {
  footerCopy: PropTypes.node,
  showPricing: PropTypes.bool,
  showTel: PropTypes.bool,
  showMorningstarLogo: PropTypes.bool,
  sidebarDataLoaded: PropTypes.bool.isRequired,
}

export default BaseFooter



// WEBPACK FOOTER //
// ./src/components/Shared/BaseFooter/BaseFooter.jsx